import React, { useState, useRef, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useTier } from '../context/TierContext';
import { TOKEN_KEY, signOut, clearUserState } from '../utils/auth';
import TickerSearch from './TickerSearch';
import useModelAccess from '../hooks/useModelAccess';
import './Navigation.css';

const SCHEME_LABELS = { neon: 'Emerald', ocean: 'Ocean', ember: 'Ember' };
const SCHEME_COLORS = {
  neon:  ['#10B981', '#00D4AA'],
  ocean: ['#7B68EE', '#00B4D8'],
  ember: ['#F97316', '#F59E0B'],
};

// Page-specific action items
const PAGE_ACTIONS = {
  '/screener': [
    { id: 'save-preset',       label: 'Save Screener Preset',    icon: '💾' },
    { id: 'load-preset',       label: 'Load Screener Preset',    icon: '📂' },
    { id: 'manage-watchlists',  label: 'Manage Watchlists',       icon: '📋' },
    { id: 'create-watchlist',   label: 'Create a Watchlist',      icon: '➕' },
    { id: 'save-watchlist',     label: 'Save to Watchlist',       icon: '⭐' },
    { id: 'export-pdf',        label: 'Export Results to PDF',    icon: '📄' },
    { id: 'share-card',        label: 'Share Score Card',         icon: '🔗' },
  ],
  '/portfolio': [
    { id: 'load-watchlist', label: 'Load Watchlist',          icon: '⭐' },
    { id: 'compare',       label: 'Compare Portfolios',       icon: '⚖️' },
    { id: 'export-pdf',    label: 'Export Analysis to PDF',   icon: '📄' },
    { id: 'share-card',    label: 'Share Score Card',         icon: '🔗' },
    { id: 'stock-news',    label: 'News for My Stocks',       icon: '📰' },
    { id: 'score-history', label: 'Score History',            icon: '📊' },
  ],
  '/trends': [
    { id: 'price-alerts',  label: 'Price Alerts',             icon: '🔔' },
  ],
  '/energy': [
    { id: 'export-pdf',    label: 'Export to PDF',            icon: '📄' },
  ],
};

const Navigation = () => {
  const { theme, toggleTheme, colorScheme, setColorScheme, COLOR_SCHEMES } = useTheme();
  const { tier, name: tierName, hasFeature } = useTier();
  const navigate = useNavigate();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [accountTab, setAccountTab] = useState('email'); // 'email' | 'password'
  const [accountForm, setAccountForm] = useState({ newEmail: '', password: '', currentPassword: '', newPassword: '', confirmNewPassword: '' });
  const [accountError, setAccountError] = useState(null);
  const [accountSuccess, setAccountSuccess] = useState(null);
  const [accountSubmitting, setAccountSubmitting] = useState(false);
  const [deleteConfirmed, setDeleteConfirmed] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const menuRef = useRef(null);
  const location = useLocation();
  const modelAccess = useModelAccess();

  // Fetch current user email when account modal opens
  useEffect(() => {
    if (showAccountModal && !userEmail) {
      const token = localStorage.getItem(TOKEN_KEY);
      if (token) {
        fetch('/api/auth/me', { headers: { Authorization: `Bearer ${token}` } })
          .then(r => r.ok ? r.json() : null)
          .then(data => { if (data) setUserEmail(data.email); })
          .catch(() => {});
      }
    }
  }, [showAccountModal, userEmail]);

  const handleAccountSubmit = async (e) => {
    e.preventDefault();
    setAccountError(null);
    setAccountSuccess(null);
    setAccountSubmitting(true);
    const token = localStorage.getItem(TOKEN_KEY);

    try {
      if (accountTab === 'email') {
        const resp = await fetch('/api/auth/update-email', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ new_email: accountForm.newEmail, password: accountForm.password }),
        });
        const data = await resp.json();
        if (!resp.ok) throw new Error(data.detail || 'Failed to update email');
        setUserEmail(data.email);
        setAccountSuccess('Email updated successfully');
        setAccountForm(f => ({ ...f, newEmail: '', password: '' }));
      } else if (accountTab === 'password') {
        if (accountForm.newPassword !== accountForm.confirmNewPassword) {
          throw new Error('New passwords do not match');
        }
        const resp = await fetch('/api/auth/update-password', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ current_password: accountForm.currentPassword, new_password: accountForm.newPassword }),
        });
        const data = await resp.json();
        if (!resp.ok) throw new Error(data.detail || 'Failed to update password');
        setAccountSuccess('Password updated successfully');
        setAccountForm(f => ({ ...f, currentPassword: '', newPassword: '', confirmNewPassword: '' }));
      } else if (accountTab === 'delete') {
        const resp = await fetch('/api/auth/delete-account', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ password: accountForm.currentPassword }),
        });
        if (!resp.ok) {
          const data = await resp.json().catch(() => ({}));
          throw new Error(data.detail || 'Failed to delete account');
        }
        // Account no longer exists — wipe local session and leave.
        clearUserState();
        signOut(); // removes token + redirects to '/'
        return;
      }
    } catch (err) {
      setAccountError(err.message);
    } finally {
      setAccountSubmitting(false);
    }
  };

  useEffect(() => {
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setSettingsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Close menu on route change
  useEffect(() => {
    setSettingsOpen(false);
    setDrawerOpen(false);
  }, [location.pathname]);

  // Lock background scroll while drawer is open
  useEffect(() => {
    if (drawerOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [drawerOpen]);

  const handleSignOut = () => {
    signOut();
  };

  const handleAction = (actionId) => {
    // Dispatch a custom event that each page can listen for
    window.dispatchEvent(new CustomEvent('settings-action', { detail: actionId }));
    setSettingsOpen(false);
  };

  const pageActions = PAGE_ACTIONS[location.pathname] || [];

  // Nav links surfaced inside the mobile drawer.
  //
  // 2026-09-11: reordered by what a NEW user can actually do. This list used to
  // run Home, Trends, Correlations, Research, Screener... so the Screener - the
  // free core of the product - sat 5th, and Correlations, which is Premium, sat
  // 3rd. A free signup opened this menu and the first distinct thing in it was
  // something they could not use. Grading, portfolio and alerts now come first;
  // the Premium surfaces sit at the bottom.
  const drawerLinks = [
    { to: '/',             label: 'Home',           end: true },
    { to: '/screener',     label: 'Screener'                },
    { to: '/portfolio',    label: 'Portfolio'               },
    { to: '/news',         label: 'News'                    },
    ...(modelAccess ? [{ to: '/model-portfolios', label: 'Model Portfolios' }] : []),
    { to: '/grade-watch',  label: 'Grade Watch'             },
    { to: '/watchlists',   label: 'Watchlists'              },
    { to: '/trends',       label: 'Trends'                  },
    { to: '/energy',       label: 'Macro'                   },
    { to: '/research',     label: 'Research'                },
    { to: '/correlations', label: 'Correlations'            },
    { to: '/alpha',        label: 'Alpha Workspace'         },
  ];

  return (
    <>
      {/* Mobile-only hamburger button (CSS hides it ≥769px) */}
      <button
        className="mobile-hamburger"
        onClick={() => setDrawerOpen(true)}
        aria-label="Open navigation menu"
        aria-expanded={drawerOpen}
        aria-controls="mobile-nav-drawer"
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="3" y1="6"  x2="21" y2="6"/>
          <line x1="3" y1="12" x2="21" y2="12"/>
          <line x1="3" y1="18" x2="21" y2="18"/>
        </svg>
      </button>

      {/* Mobile drawer (only visible when drawerOpen) */}
      {drawerOpen && (
        <>
          <div className="mobile-drawer-backdrop" onClick={() => setDrawerOpen(false)} />
          <aside id="mobile-nav-drawer" className="mobile-drawer" role="dialog" aria-label="Navigation menu">
            <div className="mobile-drawer-header">
              <span className="mobile-drawer-title"><span className="highlight">Alpha</span>Grade</span>
              <button
                className="mobile-drawer-close"
                onClick={() => setDrawerOpen(false)}
                aria-label="Close navigation menu"
              >
                ×
              </button>
            </div>
            <div className="mobile-drawer-search">
              <TickerSearch />
            </div>
            <div className="mobile-drawer-links">
              {drawerLinks.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.end}
                  className={({ isActive }) => `mobile-drawer-link ${isActive ? 'active' : ''}`}
                  onClick={() => setDrawerOpen(false)}
                >
                  {link.label}
                </NavLink>
              ))}
            </div>
          </aside>
        </>
      )}

    <nav className="main-nav" role="navigation" aria-label="Main navigation">
      <NavLink to="/" className={({ isActive }) => `nav-link home-link ${isActive ? 'active' : ''}`} end title="Home" {...(location.pathname === '/' ? { 'aria-current': 'page' } : {})}>
        <span className="nav-icon" aria-hidden="true">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
            <polyline points="9 22 9 12 15 12 15 22"/>
          </svg>
        </span>
      </NavLink>
      <NavLink to="/trends" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`} title="Market Trends" {...(location.pathname === '/trends' ? { 'aria-current': 'page' } : {})}>
        <span className="nav-icon" aria-hidden="true">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/>
            <polyline points="16 7 22 7 22 13"/>
          </svg>
        </span>
        <span className="nav-text">Trends</span>
      </NavLink>
      <NavLink to="/correlations" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`} title="Correlations (Premium)" {...(location.pathname === '/correlations' ? { 'aria-current': 'page' } : {})}>
        <span className="nav-icon" aria-hidden="true">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="7" height="7"/>
            <rect x="14" y="3" width="7" height="7"/>
            <rect x="14" y="14" width="7" height="7"/>
            <rect x="3" y="14" width="7" height="7"/>
          </svg>
        </span>
        <span className="nav-text">Correlations</span>
      </NavLink>
      <NavLink to="/research" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`} title="Market Research" {...(location.pathname === '/research' ? { 'aria-current': 'page' } : {})}>
        <span className="nav-icon" aria-hidden="true">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 18h6"/>
            <path d="M10 22h4"/>
            <path d="M12 2a7 7 0 0 0-4 12.7c.6.4 1 1.1 1 1.8V18h6v-1.5c0-.7.4-1.4 1-1.8A7 7 0 0 0 12 2z"/>
          </svg>
        </span>
        <span className="nav-text">Research</span>
      </NavLink>
      <NavLink to="/screener" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`} title="Stock Screener" {...(location.pathname === '/screener' ? { 'aria-current': 'page' } : {})}>
        <span className="nav-icon" aria-hidden="true">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
          </svg>
        </span>
        <span className="nav-text">Screener</span>
      </NavLink>
      <NavLink to="/portfolio" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`} title="Portfolio Analysis" {...(location.pathname === '/portfolio' ? { 'aria-current': 'page' } : {})}>
        <span className="nav-icon" aria-hidden="true">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
            <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
          </svg>
        </span>
        <span className="nav-text">Portfolio</span>
      </NavLink>
      <NavLink to="/news" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`} title="News on your names" {...(location.pathname === '/news' ? { 'aria-current': 'page' } : {})}>
        <span className="nav-icon" aria-hidden="true">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2zm0 0a2 2 0 0 1-2-2v-9c0-1.1.9-2 2-2h2"/>
            <line x1="10" y1="6" x2="18" y2="6"/>
            <line x1="10" y1="10" x2="18" y2="10"/>
            <line x1="10" y1="14" x2="15" y2="14"/>
          </svg>
        </span>
        <span className="nav-text">News</span>
      </NavLink>
      {modelAccess && (
        <NavLink to="/model-portfolios" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`} title="Model Portfolios" {...(location.pathname === '/model-portfolios' ? { 'aria-current': 'page' } : {})}>
          <span className="nav-icon" aria-hidden="true">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 3h6"/>
              <path d="M10 3v6L4.5 18.5A1.7 1.7 0 0 0 6 21h12a1.7 1.7 0 0 0 1.5-2.5L14 9V3"/>
              <line x1="7" y1="15" x2="17" y2="15"/>
            </svg>
          </span>
          <span className="nav-text">Models</span>
        </NavLink>
      )}
      <NavLink to="/energy" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`} title="Macro Energy" {...(location.pathname === '/energy' ? { 'aria-current': 'page' } : {})}>
        <span className="nav-icon" aria-hidden="true">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
          </svg>
        </span>
        <span className="nav-text">Macro</span>
      </NavLink>
      <NavLink to="/alpha" className={({ isActive }) => `nav-link nav-link-alpha ${isActive ? 'active' : ''}`} title="Alpha Workspace" {...(location.pathname === '/alpha' ? { 'aria-current': 'page' } : {})}>
        <span className="nav-icon" aria-hidden="true">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 3l2.39 5.49L20 9.27l-4 4.13.94 5.6L12 16.5l-4.94 2.5L8 13.4l-4-4.13 5.61-.78L12 3z"/>
          </svg>
        </span>
        <span className="nav-text">Alpha</span>
      </NavLink>

      <TickerSearch />

      <div className="settings-wrapper" ref={menuRef} onKeyDown={(e) => { if (e.key === 'Escape') setSettingsOpen(false); }}>
        <button className="settings-btn" onClick={() => setSettingsOpen(!settingsOpen)} title="Settings" aria-expanded={settingsOpen} aria-haspopup="true">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="3"/>
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
          </svg>
        </button>

        {settingsOpen && (
          <div className="settings-menu" role="menu">
            {/* Page-specific actions */}
            {pageActions.length > 0 && (
              <>
                <div className="settings-section">
                  <span className="settings-label">Actions</span>
                  {pageActions.map((action) => (
                    <button key={action.id} className="settings-option" role="menuitem" onClick={() => handleAction(action.id)}>
                      <span>{action.icon}</span>
                      <span>{action.label}</span>
                    </button>
                  ))}
                </div>
                <div className="settings-divider" />
              </>
            )}

            {/* Color scheme */}
            <div className="settings-section">
              <span className="settings-label">Color Scheme</span>
              <div className="scheme-options">
                {COLOR_SCHEMES.map((scheme) => (
                  <button
                    key={scheme}
                    className={`scheme-btn ${colorScheme === scheme ? 'active' : ''}`}
                    onClick={() => setColorScheme(scheme)}
                    title={SCHEME_LABELS[scheme]}
                  >
                    <span className="scheme-swatch" style={{ background: `linear-gradient(135deg, ${SCHEME_COLORS[scheme][0]}, ${SCHEME_COLORS[scheme][1]})` }} />
                    <span className="scheme-name">{SCHEME_LABELS[scheme]}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Appearance */}
            <div className="settings-section">
              <span className="settings-label">Appearance</span>
              <button className="settings-option" role="menuitem" onClick={toggleTheme}>
                <span>{theme === 'dark' ? '☀️' : '🌙'}</span>
                <span>{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>
              </button>
            </div>

            {/* Alerts */}
            <div className="settings-divider" />
            <div className="settings-section">
              <span className="settings-label">Alerts</span>
              <button className="settings-option" role="menuitem" onClick={() => { navigate('/grade-watch'); setSettingsOpen(false); }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                  <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                </svg>
                <span>Grade Watch</span>
              </button>
            </div>

            {/* Resources */}
            <div className="settings-divider" />
            <div className="settings-section">
              <span className="settings-label">Resources</span>
              <button className="settings-option" role="menuitem" onClick={() => { navigate('/faq'); setSettingsOpen(false); }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
                <span>Help & FAQ</span>
              </button>
              <button className="settings-option" role="menuitem" onClick={() => { navigate('/how-it-works'); setSettingsOpen(false); }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="20" x2="18" y2="10"/>
                  <line x1="12" y1="20" x2="12" y2="4"/>
                  <line x1="6" y1="20" x2="6" y2="14"/>
                </svg>
                <span>How Scores Work</span>
              </button>
              <button className="settings-option" role="menuitem" onClick={() => { navigate('/glossary'); setSettingsOpen(false); }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                </svg>
                <span>Glossary</span>
              </button>
              <button className="settings-option" role="menuitem" onClick={() => { navigate('/contact'); setSettingsOpen(false); }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
                <span>Contact / Feedback</span>
              </button>
            </div>

            {/* Alpha (Premium only) */}
            {hasFeature && hasFeature('alpha_memory') && (
              <>
                <div className="settings-divider" />
                <div className="settings-section">
                  <span className="settings-label">Alpha</span>
                  <button className="settings-option" role="menuitem" onClick={() => { navigate('/alpha'); setSettingsOpen(false); }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                      <line x1="9" y1="10" x2="9" y2="10"/>
                      <line x1="12" y1="10" x2="12" y2="10"/>
                      <line x1="15" y1="10" x2="15" y2="10"/>
                    </svg>
                    <span>Alpha Workspace</span>
                  </button>
                  <button className="settings-option" role="menuitem" onClick={() => { navigate('/alpha/memory'); setSettingsOpen(false); }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="9"/>
                      <path d="M12 7v5l3 3"/>
                    </svg>
                    <span>What Alpha Remembers</span>
                  </button>
                </div>
              </>
            )}

            {/* Subscription */}
            <div className="settings-divider" />
            <div className="settings-section">
              <span className="settings-label">Subscription</span>
              <button className="settings-option" role="menuitem" onClick={() => { navigate('/pricing'); setSettingsOpen(false); }}>
                <span>⭐</span>
                <span>Your Plan: <strong style={{ color: tier === 'free' ? '#94A3B8' : '#6495ED' }}>{tierName}</strong></span>
              </button>
              {tier !== 'free' && (
                <button className="settings-option" role="menuitem" onClick={async () => {
                  setSettingsOpen(false);
                  try {
                    const token = localStorage.getItem(TOKEN_KEY);
                    const resp = await fetch('/api/subscription/create-portal', {
                      method: 'POST',
                      headers: { Authorization: `Bearer ${token}` },
                    });
                    if (resp.ok) {
                      const data = await resp.json();
                      window.location.href = data.portal_url;
                    }
                  } catch {}
                }}>
                  <span>💳</span>
                  <span>Manage Billing</span>
                </button>
              )}
            </div>

            <div className="settings-divider" />

            <button className="settings-option" role="menuitem" onClick={() => { setShowAccountModal(true); setSettingsOpen(false); setAccountError(null); setAccountSuccess(null); }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
              <span>Account Settings</span>
            </button>

            <button className="settings-option sign-out-option" role="menuitem" onClick={handleSignOut}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                <polyline points="16 17 21 12 16 7"/>
                <line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
              <span>Sign Out</span>
            </button>
          </div>
        )}
      </div>

      {/* Account Settings Modal */}
      {showAccountModal && (
        <div className="account-modal-overlay" onClick={() => setShowAccountModal(false)}>
          <div className="account-modal" onClick={(e) => e.stopPropagation()}>
            <div className="account-modal-header">
              <h3>Account Settings</h3>
              <button className="account-modal-close" onClick={() => setShowAccountModal(false)}>&times;</button>
            </div>

            <div className="account-current-email">
              Signed in as <strong>{userEmail || '...'}</strong>
            </div>

            <div className="account-tabs">
              <button
                className={`account-tab ${accountTab === 'email' ? 'active' : ''}`}
                onClick={() => { setAccountTab('email'); setAccountError(null); setAccountSuccess(null); }}
              >
                Change Email
              </button>
              <button
                className={`account-tab ${accountTab === 'password' ? 'active' : ''}`}
                onClick={() => { setAccountTab('password'); setAccountError(null); setAccountSuccess(null); }}
              >
                Change Password
              </button>
              <button
                className={`account-tab account-tab-danger ${accountTab === 'delete' ? 'active' : ''}`}
                onClick={() => { setAccountTab('delete'); setAccountError(null); setAccountSuccess(null); setDeleteConfirmed(false); }}
              >
                Delete Account
              </button>
            </div>

            <form className="account-form" onSubmit={handleAccountSubmit}>
              {accountTab === 'email' ? (
                <>
                  <div className="account-field">
                    <label htmlFor="acc-new-email">New Email</label>
                    <input
                      id="acc-new-email"
                      type="email"
                      autoComplete="off"
                      value={accountForm.newEmail}
                      onChange={(e) => setAccountForm(f => ({ ...f, newEmail: e.target.value }))}
                      placeholder="new@example.com"
                      required
                      disabled={accountSubmitting}
                    />
                  </div>
                  <div className="account-field">
                    <label htmlFor="acc-password">Current Password</label>
                    <input
                      id="acc-password"
                      type="password"
                      autoComplete="new-password"
                      value={accountForm.password}
                      onChange={(e) => setAccountForm(f => ({ ...f, password: e.target.value }))}
                      placeholder="Enter current password"
                      required
                      disabled={accountSubmitting}
                    />
                  </div>
                </>
              ) : accountTab === 'delete' ? (
                <>
                  <div className="account-danger-note">
                    <strong>This permanently deletes your account.</strong> Your portfolios,
                    watchlists, Alpha history, and settings will be erased and cannot be
                    recovered. Any active subscription is cancelled automatically.
                  </div>
                  <div className="account-field">
                    <label htmlFor="acc-del-pw">Confirm Password</label>
                    <input
                      id="acc-del-pw"
                      type="password"
                      autoComplete="new-password"
                      value={accountForm.currentPassword}
                      onChange={(e) => setAccountForm(f => ({ ...f, currentPassword: e.target.value }))}
                      placeholder="Enter your password"
                      required
                      disabled={accountSubmitting}
                    />
                  </div>
                  <label className="account-danger-check">
                    <input
                      type="checkbox"
                      checked={deleteConfirmed}
                      onChange={(e) => setDeleteConfirmed(e.target.checked)}
                      disabled={accountSubmitting}
                    />
                    <span>I understand this is permanent and cannot be undone.</span>
                  </label>
                </>
              ) : (
                <>
                  <div className="account-field">
                    <label htmlFor="acc-cur-pw">Current Password</label>
                    <input
                      id="acc-cur-pw"
                      type="password"
                      autoComplete="new-password"
                      value={accountForm.currentPassword}
                      onChange={(e) => setAccountForm(f => ({ ...f, currentPassword: e.target.value }))}
                      placeholder="Enter current password"
                      required
                      disabled={accountSubmitting}
                    />
                  </div>
                  <div className="account-field">
                    <label htmlFor="acc-new-pw">New Password</label>
                    <input
                      id="acc-new-pw"
                      type="password"
                      autoComplete="new-password"
                      value={accountForm.newPassword}
                      onChange={(e) => setAccountForm(f => ({ ...f, newPassword: e.target.value }))}
                      placeholder="Enter new password"
                      required
                      disabled={accountSubmitting}
                    />
                  </div>
                  <div className="account-field">
                    <label htmlFor="acc-confirm-pw">Confirm New Password</label>
                    <input
                      id="acc-confirm-pw"
                      type="password"
                      autoComplete="new-password"
                      value={accountForm.confirmNewPassword}
                      onChange={(e) => setAccountForm(f => ({ ...f, confirmNewPassword: e.target.value }))}
                      placeholder="Re-enter new password"
                      required
                      disabled={accountSubmitting}
                    />
                  </div>
                </>
              )}

              {accountError && <div className="account-error">{accountError}</div>}
              {accountSuccess && <div className="account-success">{accountSuccess}</div>}

              {accountTab === 'delete' ? (
                <button
                  type="submit"
                  className="account-submit-btn account-delete-btn"
                  disabled={accountSubmitting || !deleteConfirmed || !accountForm.currentPassword}
                >
                  {accountSubmitting ? 'Deleting…' : 'Permanently delete my account'}
                </button>
              ) : (
                <button type="submit" className="account-submit-btn" disabled={accountSubmitting}>
                  {accountSubmitting ? 'Updating...' : accountTab === 'email' ? 'Update Email' : 'Update Password'}
                </button>
              )}
            </form>
          </div>
        </div>
      )}
    </nav>
    </>
  );
};

export default Navigation;
